import { useEffect, useState } from 'react';
import tw from 'twin.macro';
import { useTranslation } from 'react-i18next';

import { Dropdown, IDropdownOption, Option } from '@components/Dropdown';
import {
  SegmentCreatorData,
  useSegmentCreatorContext,
} from '@insights_module/context/SegmentCreatorContext';
import { AQLQueryCondition, AQLQueryType } from '@insights_module/adc_ql/types';
import { FilterInput } from '@insights_module/components/FilterInput';
import { useFilterTypeOptions } from '@insights_module/hooks/useFilterTypeOptions';

export const SegmentCreatorTranscriptItem = ({
  segmentData,
}: {
  segmentData: SegmentCreatorData;
}) => {
  const { condition, value, operator } = segmentData;
  const { t } = useTranslation();
  const { pushSegmentData, isPresentational } = useSegmentCreatorContext();
  const { dropdownOptions } = useFilterTypeOptions();

  const conditionOptions: IDropdownOption[] = [
    {
      value: 'includes',
      label: <Option>{t('insights.query_builder.conditions.mention')}</Option>,
    },
    {
      value: 'excludes',
      label: <Option>{t('insights.query_builder.conditions.does_not_mention')}</Option>,
    },
  ];

  const [selectedType, setSelectedType] = useState<IDropdownOption>(
    dropdownOptions.find((item) => item.value === segmentData.type) || dropdownOptions[0]
  );
  const [selectedCondition, setSelectedCondition] = useState<IDropdownOption>(
    conditionOptions.find(
      (item) => item.value === (condition as unknown as string) && item.meta?.operator === operator
    ) || conditionOptions[0]
  );
  const [selectedEpisodeTitle, setSelectedTranscriptMention] = useState<string>(
    (value as string) || ''
  );

  useEffect(() => {
    if (selectedType) {
      const type = selectedType.value as string;
      pushSegmentData({
        ...segmentData,
        type: type as AQLQueryType,
        category: null,
        value: null,
        condition: null,
        negation: false,
      });
    }
  }, [selectedType]);

  useEffect(() => {
    if (selectedCondition && selectedEpisodeTitle) {
      const segment = {
        ...segmentData,
        condition: selectedCondition.value as unknown as Exclude<
          AQLQueryCondition,
          'includes' | 'excludes'
        >,
        value: selectedEpisodeTitle,
      };
      pushSegmentData(segment);
    }
  }, [selectedCondition, selectedEpisodeTitle]);

  return (
    <div tw='flex gap-1 items-center'>
      <Dropdown
        options={dropdownOptions}
        value={selectedType}
        onChange={(val) => setSelectedType(val as IDropdownOption)}
        labelStyle={tw`bg-spec-transcript-filter-bg border border-spec-transcript-filter-stroke text-spec-transcript-filter-text font-semibold`}
        caretStyle={tw`fill-spec-transcript-filter-text`}
        dropdownMenuStyle={tw`w-[178px]`}
        isPresentational={isPresentational}
      />

      <Dropdown
        //for now we don't want to show is and is not conditions on creating named entities
        options={conditionOptions.slice(0, 2)}
        value={selectedCondition}
        onChange={(val) => setSelectedCondition(val as IDropdownOption)}
        isPresentational={isPresentational}
      />

      <FilterInput
        focus
        value={selectedEpisodeTitle}
        contentEditable={!isPresentational}
        placeholder={t('insights.query_builder.transcript.placeholder')}
        onPressEnter={(value) => setSelectedTranscriptMention(value as string)}
      />
    </div>
  );
};
